'use client';

/**
 * Sign-in page (passwordless, email + 6-digit code).
 *
 * Mirrors lexai-superadmin/src/app/login/page.tsx — same form structure,
 * same Meridian classes (.eyebrow / .h-display-3 / .input / .btn).
 *
 * Step 1: POST /auth/login/request-code  → always 200, anti-enumeration.
 * Step 2: POST /auth/login/verify-code   → JWT.
 */
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { type FormEvent, useState } from 'react';

import { ApiError, AuthError, NotFoundError, RateLimitError } from '@/lib/api';
import { requestLoginCode, safeRedirectPath, verifyLoginCode } from '@/lib/auth';

type Step = 'email' | 'code';

export default function SignInPage() {
  const router = useRouter();
  const params = useSearchParams();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [noAccount, setNoAccount] = useState(false);

  async function onRequestCode(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    setInfo(null);

    const normalised = email.trim().toLowerCase();
    setNoAccount(false);
    try {
      await requestLoginCode(normalised);
      setEmail(normalised);
      setStep('code');
      setInfo('We sent a 6-digit code to your email. It expires in 10 minutes.');
    } catch (e2) {
      if (e2 instanceof NotFoundError) {
        setNoAccount(true);
        setErr('No account found with this email.');
      } else if (e2 instanceof RateLimitError) {
        setErr('Too many attempts. Try again in 15 minutes.');
      } else if (e2 instanceof ApiError) {
        setErr(e2.message);
      } else {
        setErr('Could not send code.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyCode(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);

    try {
      await verifyLoginCode(email, code.trim());
      router.replace(safeRedirectPath(params.get('redirect_url')));
    } catch (e2) {
      if (e2 instanceof AuthError) {
        setErr('Invalid or expired code.');
      } else if (e2 instanceof RateLimitError) {
        setErr('Too many attempts. Request a new code.');
      } else if (e2 instanceof ApiError) {
        setErr(e2.message);
      } else {
        setErr('Login failed.');
      }
      setBusy(false);
    }
  }

  async function onResend(): Promise<void> {
    if (resending) return;
    setResending(true);
    setErr(null);
    setInfo(null);
    try {
      await requestLoginCode(email);
      setInfo('A new code is on its way.');
    } catch (e2) {
      if (e2 instanceof ApiError) setErr(e2.message);
      else setErr('Could not resend the code.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div>
      <div className="eyebrow mb-2" style={{ color: 'var(--cu)' }}>
        Welcome back
      </div>
      <h1 className="h-display-3 mb-2" style={{ fontSize: 30, lineHeight: 1.1 }}>
        Access your <em>legal intelligence</em> workspace.
      </h1>
      <p className="muted text-[13px] mb-6">
        {step === 'email'
          ? 'Enter your work email — we’ll send a one-time code.'
          : `We sent a code to ${email}. Enter it below to sign in.`}
      </p>

      {step === 'email' ? (
        <form onSubmit={onRequestCode}>
          <div className="flex flex-col gap-1 mb-4">
            <label className="eyebrow" htmlFor="signin-email">
              Email
            </label>
            <input
              autoFocus
              required
              autoComplete="email"
              className={`input${err ? ' is-error' : ''}`}
              id="signin-email"
              name="email"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
            {err ? (
              <span className="text-[12px]" style={{ color: 'var(--red)' }}>
                {err}
                {noAccount ? (
                  <>
                    {' '}
                    <Link
                      className="lex-auth-link"
                      href={`/sign-up?email=${encodeURIComponent(email.trim().toLowerCase())}`}
                      style={{ color: 'var(--cu)' }}
                    >
                      Sign up instead →
                    </Link>
                  </>
                ) : null}
              </span>
            ) : null}
          </div>
          <button
            className="btn btn-p btn-lg"
            disabled={busy}
            style={{ width: '100%' }}
            type="submit"
          >
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      ) : (
        <form onSubmit={onVerifyCode}>
          <div className="flex flex-col gap-1 mb-4">
            <label className="eyebrow" htmlFor="signin-code">
              6-digit code
            </label>
            <input
              autoFocus
              required
              autoComplete="one-time-code"
              className={`input${err ? ' is-error' : ''}`}
              id="signin-code"
              inputMode="numeric"
              maxLength={6}
              minLength={6}
              name="code"
              pattern="\d{6}"
              style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: 18 }}
              type="text"
              value={code}
              onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            {info ? (
              <span className="muted text-[12px]" style={{ marginTop: 4 }}>
                {info}
              </span>
            ) : null}
            {err ? (
              <span className="text-[12px]" style={{ color: 'var(--red)' }}>
                {err}
              </span>
            ) : null}
          </div>
          <button
            className="btn btn-p btn-lg"
            disabled={busy || code.length !== 6}
            style={{ width: '100%' }}
            type="submit"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <div
            className="text-[12px]"
            style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}
          >
            <button
              className="lex-auth-link"
              disabled={resending}
              type="button"
              onClick={onResend}
            >
              {resending ? 'Sending…' : 'Resend code'}
            </button>
            <button
              className="lex-auth-link is-muted"
              type="button"
              onClick={() => {
                setStep('email');
                setCode('');
                setErr(null);
                setInfo(null);
              }}
            >
              Use a different email
            </button>
          </div>
        </form>
      )}

      {/* Signing up is the other half of this screen's job, and a 12px inline
          link at the bottom of a card reads as a footnote. A labelled rule and
          an outline button give it the weight of a choice without competing
          with the primary action — the same shape every account screen uses.
          Hidden on the code step: by then the visitor has committed, and the
          offer is only noise. */}
      {step === 'email' ? (
        <>
          <div className="lex-auth-alt">
            {/* The wordmark itself, not the word. alt carries the brand name so
                the sentence still reads "New to LexVio.ai?" when spoken. */}
            <span>
              New to{' '}
              <Image
                alt="LexVio.ai"
                className="lex-auth-alt__logo"
                height={16}
                src="/logo.png"
                width={43}
              />
              ?
            </span>
          </div>
          <Link
            className="btn btn-s btn-lg"
            href="/sign-up"
            style={{ width: '100%' }}
          >
            Create an account
          </Link>
        </>
      ) : null}
    </div>
  );
}
